import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { FTPAccount } from '../../types';
import { ClayDatePicker } from '../UI/ClayDatePicker';
import { ClaySelect, ClaySelectOption } from '../UI/ClaySelect';
import styles from './GalleryFiltersPanel.module.css';

export interface GalleryFilters {
  search: string;
  photographerId: string;
  fromDate: string;
  toDate: string;
  sort: 'asc' | 'desc';
}

interface GalleryFiltersPanelProps {
  filters: GalleryFilters;
  ftpAccounts: FTPAccount[];
  hasManualUploads?: boolean;
  onChange: (filters: GalleryFilters) => void;
  onReset: () => void;
}

function toYMD(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function fmtDate(ymd: string): string {
  if (!ymd) return '';
  const d = new Date(ymd + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const MANUAL_UPLOAD_KEY = '_manual_';

export function GalleryFiltersPanel({
  filters,
  ftpAccounts,
  hasManualUploads,
  onChange,
  onReset,
}: GalleryFiltersPanelProps) {
  const set = (patch: Partial<GalleryFilters>) => onChange({ ...filters, ...patch });

  // Start collapsed on mobile, expanded on desktop
  const [bodyOpen, setBodyOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 640 : true
  );

  const hasActiveFilters =
    filters.search !== '' ||
    filters.photographerId !== 'all' ||
    filters.fromDate !== '' ||
    filters.toDate !== '' ||
    filters.sort !== 'desc';

  const activeCount = [
    filters.search !== '',
    filters.photographerId !== 'all',
    filters.fromDate !== '',
    filters.toDate !== '',
    filters.sort !== 'desc',
  ].filter(Boolean).length;

  // Active filter chips (excludes search — it has its own inline clear)
  const chips = [
    filters.fromDate ? {
      key: 'from',
      label: `From ${fmtDate(filters.fromDate)}`,
      clear: () => set({ fromDate: '' }),
    } : null,
    filters.toDate ? {
      key: 'to',
      label: `To ${fmtDate(filters.toDate)}`,
      clear: () => set({ toDate: '' }),
    } : null,
    filters.photographerId !== 'all' ? {
      key: 'photographer',
      label: filters.photographerId === MANUAL_UPLOAD_KEY
        ? 'Manual Upload'
        : ftpAccounts.find(a => a._id === filters.photographerId)?.photographerName
          || ftpAccounts.find(a => a._id === filters.photographerId)?.username
          || 'Photographer',
      clear: () => set({ photographerId: 'all' }),
    } : null,
    filters.sort !== 'desc' ? {
      key: 'sort',
      label: 'Oldest first',
      clear: () => set({ sort: 'desc' }),
    } : null,
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  const today = toYMD(new Date());
  const yesterday = toYMD(new Date(new Date().getTime() - 86_400_000));

  return (
    <motion.div
      className={styles.panel}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className={styles['panel-header']}>
        <button
          className={styles['toggle-btn']}
          onClick={() => setBodyOpen(o => !o)}
          aria-expanded={bodyOpen}
        >
          <SlidersHorizontal className={styles['icon-filter']} />
          <span className={styles['panel-title']}>Filters</span>
          {!bodyOpen && activeCount > 0 && (
            <span className={styles['active-count']}>{activeCount}</span>
          )}
          <motion.span
            animate={{ rotate: bodyOpen ? 180 : 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            className={styles['icon-chevron-wrapper']}
          >
            <ChevronDown className={styles['icon-chevron']} />
          </motion.span>
        </button>

        <AnimatePresence>
          {hasActiveFilters && (
            <motion.button
              onClick={onReset}
              className={styles['reset-btn']}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              whileTap={{ scale: 0.92 }}
            >
              <X className={styles['icon-reset']} />
              Reset all
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* ── Collapsible body ──────────────────────────────────────────── */}
      <AnimatePresence initial={false}>
        {bodyOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
            className={styles['overflow-hidden']}
          >
            <div className={styles.body}>

              {/* Quick presets */}
              <div className={styles['preset-row']}>
                <span className={styles['preset-label']}>Quick:</span>
                {[
                  { label: 'Today', action: () => set({ fromDate: today, toDate: today }) },
                  { label: 'Yesterday', action: () => set({ fromDate: yesterday, toDate: yesterday }) },
                ].map(({ label, action }) => (
                  <motion.button
                    key={label}
                    type="button"
                    onClick={action}
                    className={styles['preset-btn']}
                    whileTap={{ scale: 0.92 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    {label}
                  </motion.button>
                ))}
              </div>

              {/* Search */}
              <div className={styles['search-row']}>
                <Search className={styles['search-icon']} />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => set({ search: e.target.value })}
                  placeholder="Search by filename or photographer…"
                  className={styles['search-input']}
                />
                <AnimatePresence>
                  {filters.search && (
                    <motion.button
                      className={styles['search-clear']}
                      onClick={() => set({ search: '' })}
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.7 }}
                      transition={{ duration: 0.12 }}
                      whileTap={{ scale: 0.88 }}
                    >
                      <X className={styles['icon-search-clear']} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Filter grid */}
              <div className={styles.grid}>
                <ClayDatePicker
                  label="From date"
                  value={filters.fromDate}
                  onChange={(v) => set({ fromDate: v })}
                  placeholder="Start date"
                />
                <ClayDatePicker
                  label="To date"
                  value={filters.toDate}
                  onChange={(v) => set({ toDate: v })}
                  placeholder="End date"
                />
                <ClaySelect
                  label="Photographer"
                  value={filters.photographerId}
                  onChange={(v) => set({ photographerId: v })}
                  options={[
                    { value: 'all', label: 'All photographers' },
                    ...ftpAccounts.map((acc): ClaySelectOption => ({
                      value: acc._id,
                      label: acc.photographerName || acc.username,
                    })),
                    ...(hasManualUploads ? [{ value: MANUAL_UPLOAD_KEY, label: 'Manual Upload' }] : []),
                  ]}
                />
                <ClaySelect
                  label="Sort by upload"
                  value={filters.sort}
                  onChange={(v) => set({ sort: v as 'asc' | 'desc' })}
                  options={[
                    { value: 'desc', label: 'Newest first' },
                    { value: 'asc', label: 'Oldest first' },
                  ]}
                />
              </div>

              {/* Active filter chips */}
              <AnimatePresence>
                {chips.length > 0 && (
                  <motion.div
                    className={styles.chips}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    {chips.map((chip) => (
                      <motion.button
                        key={chip.key}
                        type="button"
                        onClick={chip.clear}
                        className={styles.chip}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                        whileTap={{ scale: 0.92 }}
                      >
                        {chip.label}
                        <X className={styles['icon-chip']} />
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
